import type { StackProps } from '@mui/material';

import { styled } from '@mend/mui';
import { Cancel, FileDownload, FilterAlt, Info, Search, ViewColumn } from '@mui/icons-material';
import { Divider, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { mergeSlotProps } from '@mui/material/utils';
import {
  ColumnsPanelTrigger,
  Toolbar as DGToolbar,
  ExportCsv,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  ToolbarButton,
} from '@mui/x-data-grid-pro';

import { useMenu } from '#hooks/menu.ts';
import { SEARCH_MIN_CHARS } from './Toolbar.utils.ts';

const StyledToolbar = styled(DGToolbar)(({ theme }) => ({
  '--toolbar-gap': 'calc(var(--DataGrid-t-spacing-unit) * 0.5)', // 4px
  minHeight: 'auto',
  padding: 'calc(var(--DataGrid-t-spacing-unit) * 2)', // 16px
  gap: 'var(--toolbar-gap)',
  flexDirection: 'column',
  alignItems: 'flex-end',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const StyledStack = styled((props: StackProps) => <Stack spacing={0} {...props} />)({
  gap: 'var(--toolbar-gap)',
});

const StyledQuickFilter = styled(QuickFilter)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: '260px',
  },
}));

/**
 * Custom toolbar for drilldown components/routes. The search bar was built
 * using mui's docs as reference, with some changes required for the material
 * version we use as of TS-15810 (v5).
 *
 * @see https://mui.com/x/react-data-grid/components/toolbar/#basic-usage
 * @see https://mui.com/x/react-data-grid/components/quick-filter/#persistent-quick-filter
 */
export function Toolbar() {
  return (
    <StyledToolbar>
      <StyledStack>
        <Tooltip title="Columns">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumn fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>

        <Tooltip title="Filters">
          <FilterPanelTrigger render={<ToolbarButton />}>
            <FilterAlt fontSize="small" />
          </FilterPanelTrigger>
        </Tooltip>

        <Divider orientation="vertical" variant="middle" flexItem />
        <ExportMenu />
      </StyledStack>

      <StyledQuickFilter expanded>
        <QuickFilterControl
          render={({ ref, ...other }) => (
            <TextField
              {...other}
              inputRef={ref}
              fullWidth
              aria-label="Search all fields"
              placeholder="Search all fields..."
              size="small"
              slotProps={{
                ...other.slotProps,
                input: mergeSlotProps(other.slotProps?.input, {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {other.value && other.value.trim().length >= SEARCH_MIN_CHARS ? (
                        <QuickFilterClear
                          aria-label="Clear search"
                          edge="end"
                          size="small"
                          material={{ sx: { marginRight: -0.75 } }}
                        >
                          <Cancel fontSize="small" />
                        </QuickFilterClear>
                      ) : (
                        <Tooltip title={`Type ${SEARCH_MIN_CHARS} characters to begin searching`}>
                          <Info
                            fontSize="small"
                            sx={{
                              // One-off value to align it with the icon of the button above
                              marginRight: -0.1,
                              // To make a distinction between this non-interactive element and the
                              // button above
                              opacity: 0.75,
                            }}
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }),
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </StyledToolbar>
  );
}

export function ExportToolbar() {
  return (
    <StyledToolbar>
      <StyledStack>
        <ExportMenu />
      </StyledStack>
    </StyledToolbar>
  );
}

function ExportMenu() {
  const exportMenu = useMenu();

  return (
    <>
      <Tooltip title="Export">
        <ToolbarButton {...exportMenu.getTriggerProps()}>
          <FileDownload fontSize="small" />
        </ToolbarButton>
      </Tooltip>

      <Menu
        {...exportMenu.getMenuProps()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ExportCsv render={<MenuItem />} onClick={exportMenu.close}>
          Download as CSV
        </ExportCsv>
      </Menu>
    </>
  );
}
